import Dropdown from "component/form/dropDown";
import Input from "component/form/Input";
import OnboardingLayout from "component/auth/OnboardingLayout";
import UploadInputField from "component/auth/onboarding/UploadInputField";
import SampleDocumentModal from "component/auth/onboarding/SampleDocumentModal";
import { identificationSampleDocuments } from "component/auth/onboarding/onboardingSampleDocuments";
import {
    handleAddBusinessUbo,
    handleFetchBusinessById,
    handleFetchKybCountries,
    handleUploadOnboardingDocument,
} from "api/onboarding";
import { CheckCircleIcon } from "asset/icons";
import { InfoCircle } from "iconsax-reactjs";
import ErrorMessage from "../ErrorMessage";
import Loader from "component/loader";
import InfoTooltip from "component/InfoTooltip";
import { Country } from "country-state-city";
import { useCallback, useEffect, useState, Fragment } from "react";
import { Helmet } from "react-helmet";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { cn } from "lib/utils";

const steps = [
    { title: "Business information" },
    { title: "Business verification" },
    { title: "Business ownership", subtitles: ["Control person", "Directors"] },
];

const genderOptions = [
    { label: "Male", value: "male" },
    { label: "Female", value: "female" },
];

const identificationOptions = [
    { label: "Passport", value: "passport" },
    { label: "National ID", value: "national_id" },
    { label: "Driver's License", value: "drivers_license" },
];

const createInitialForm = (phoneCountry = "") => ({
    firstName: "",
    lastName: "",
    email: "",
    phoneCountry,
    phoneNumber: "",
    title: "",
    ownershipShare: "",
    bvn: "",
    gender: "",
    dateOfBirth: "",
    identificationType: "passport",
    identificationNumber: "",
    identificationDocumentFront: null,
    identificationDocumentBack: null,
    streetAddress: "",
    city: "",
    state: "",
    postalCode: "",
    isDirector: true,
});

const normalizeCountriesResponse = (responseBody) =>
    (Array.isArray(responseBody?.data) && responseBody.data) ||
    (Array.isArray(responseBody?.data?.countries) && responseBody.data.countries) ||
    (Array.isArray(responseBody?.countries) && responseBody.countries) ||
    (Array.isArray(responseBody) && responseBody) ||
    [];

const toApiIdentificationType = (type) =>
    ({
        passport: "passport",
        national_id: "nationalId",
        drivers_license: "driversLicense",
    })[type] || type;

const extractHouseNumber = (street = "") => {
    const match = street.trim().match(/^(\d+[A-Za-z-]?)/);
    return match ? match[1] : "";
};

const getRegisteredBusinessCountryCode = (business = {}) => {
    if (business?.businessCountry?.code) return String(business.businessCountry.code).toUpperCase();
    if (business?.countryCode) return String(business.countryCode).toUpperCase();
    const countryName = business?.businessCountry?.name || business?.country;
    return (
        Country.getAllCountries().find(
            (country) =>
                country.name.toLowerCase() ===
                String(countryName || "")
                    .trim()
                    .toLowerCase()
        )?.isoCode || ""
    );
};

const getRegisteredBusinessCountryName = (business = {}) =>
    business?.businessCountry?.name || business?.country || "";

const normalizeCountryName = (countryName) =>
    String(countryName || "")
        .trim()
        .toLowerCase();

const isNigerianCountry = (countryCode) => String(countryCode || "").toUpperCase() === "NG";

const formatDateValue = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const getMaxDateOfBirth = () => {
    const date = new Date();
    date.setDate(date.getDate() - 1);
    return formatDateValue(date);
};
const isValidBvn = (value) => /^\d{11}$/.test(String(value || ""));
const parseOwnershipShare = (value) => {
    const ownership = Number(String(value ?? "").replace(/[,%\s]/g, ""));
    return Number.isFinite(ownership) ? ownership : 0;
};
const normalizeOwnershipInput = (value) => {
    const cleaned = String(value || "").replace(/[^\d.]/g, "");
    const [whole, ...decimalParts] = cleaned.split(".");
    return decimalParts.length ? `${whole}.${decimalParts.join("")}` : whole;
};
const isOwnershipShareValid = (value) => {
    if (!String(value || "").trim()) return false;
    const ownership = parseOwnershipShare(value);
    return ownership >= 25 && ownership <= 100;
};
const getUboOwnership = (ubo) => ubo.percentageOwnership || ubo.ownershipShare || "0";
const fromApiIdentificationType = (type) =>
    ({
        passport: "passport",
        nationalid: "national_id",
        national_id: "national_id",
        driverslicense: "drivers_license",
        drivers_license: "drivers_license",
    })[
        String(type || "")
            .replace(/\s/g, "")
            .toLowerCase()
    ] || "passport";
const asExistingFile = (raw, fallbackName) => {
    const url = (typeof raw === "string" && raw) || raw?.url || raw?.url?.link || raw?.link || "";
    if (!url) return null;
    return {
        name: raw?.name || raw?.key || fallbackName,
        size: raw?.size || 0,
        url,
        key: raw?.key || "",
    };
};
const mapUboToForm = (ubo, phoneCountry = "", phoneCountryOptions = []) => {
    if (!ubo) return createInitialForm(phoneCountry || "");

    const identificationType = fromApiIdentificationType(
        ubo?.meanOfIdentificationType || ubo?.meansOfIdentification || ubo?.identificationType
    );

    const frontUrl =
        ubo?.idDocumentFront?.url ||
        ubo?.meanOfIdentificationUrl ||
        ubo?.identificationDocument?.url ||
        ubo?.identificationDocumentFront?.url ||
        (typeof ubo?.idDocumentFront === "string" ? ubo.idDocumentFront : "") ||
        "";
    const frontKey =
        ubo?.idDocumentFront?.key ||
        ubo?.meanOfIdentificationKey ||
        ubo?.identificationDocument?.key ||
        ubo?.identificationDocumentFront?.key ||
        "Identification front";

    const backUrl =
        ubo?.idDocumentBack?.url ||
        ubo?.identificationDocumentBack?.url ||
        (typeof ubo?.idDocumentBack === "string" ? ubo.idDocumentBack : "") ||
        "";
    const backKey =
        ubo?.idDocumentBack?.key ||
        ubo?.identificationDocumentBack?.key ||
        "Identification back";

    let uboPhoneCountry = ubo?.phoneCountry || phoneCountry || "";
    let rawPhone = String(ubo?.phoneNumber || "").trim();

    if (rawPhone.startsWith("+") && phoneCountryOptions.length > 0) {
        const matchingCountry = [...phoneCountryOptions]
            .sort((a, b) => b.dialCode.length - a.dialCode.length)
            .find((option) => rawPhone.startsWith(option.dialCode));
        if (matchingCountry) {
            uboPhoneCountry = matchingCountry.code;
            rawPhone = rawPhone.slice(matchingCountry.dialCode.length);
        }
    } else if (rawPhone.startsWith("+")) {
        rawPhone = rawPhone.replace(/^\+/, "");
    }

    return {
        ...createInitialForm(uboPhoneCountry || ""),
        firstName: ubo?.firstName || "",
        lastName: ubo?.lastName || "",
        email: ubo?.email || "",
        phoneCountry: uboPhoneCountry,
        phoneNumber: rawPhone,
        title: ubo?.title || "",
        ownershipShare: String(ubo?.percentageOwnership || ubo?.ownershipShare || ""),
        bvn: ubo?.bvnNumber || ubo?.bvn || "",
        gender: String(ubo?.gender || "").toLowerCase(),
        dateOfBirth: String(ubo?.dateOfBirth || "").slice(0, 10),
        identificationType,
        identificationNumber:
            ubo?.idNumber || ubo?.meanOfIdentificationNumber || ubo?.identificationNumber || "",
        identificationDocumentFront: asExistingFile(
            frontUrl ? { url: frontUrl, key: frontKey, name: frontKey } : (ubo?.idDocumentFront || ubo?.meanOfIdentificationUrl || ubo?.identificationDocument),
            "Identification front"
        ),
        identificationDocumentBack: asExistingFile(
            backUrl ? { url: backUrl, key: backKey, name: backKey } : ubo?.idDocumentBack,
            "Identification back"
        ),
        streetAddress:
            ubo?.address?.street ||
            ubo?.residentialAddress?.street ||
            ubo?.residentialAddress?.streetAddress ||
            ubo?.streetAddress ||
            "",
        city: ubo?.address?.city || ubo?.residentialAddress?.city || ubo?.city || "",
        state: ubo?.address?.state || ubo?.residentialAddress?.state || ubo?.state || "",
        postalCode:
            ubo?.address?.zipCode ||
            ubo?.residentialAddress?.postalCode ||
            ubo?.residentialAddress?.zipCode ||
            ubo?.postalCode ||
            "",
        isDirector: ubo?.isDirector !== false,
        targetId: ubo?.targetId || ubo?._id || ubo?.id || ubo?.email || "",
    };
};

const ControlPerson = ({ currentStep = 2, isUpdating, handleUpdateMoreData, prefillData }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const shouldReturnToSummary =
        new URLSearchParams(location.search).get("returnTo") === "summary";
    const currentBusiness = useSelector((state) => state);
    const businessId = currentBusiness.businessid || currentBusiness.business?._id;
    const reduxBusinessCountryCode = getRegisteredBusinessCountryCode(currentBusiness.business);
    const reduxBusinessCountryName = getRegisteredBusinessCountryName(currentBusiness.business);
    const [businessCountryCode, setBusinessCountryCode] = useState(reduxBusinessCountryCode);
    const [businessCountryName, setBusinessCountryName] = useState(reduxBusinessCountryName);
    const businessRequiresBvn = isNigerianCountry(businessCountryCode);
    const [form, setForm] = useState(() => createInitialForm(businessCountryCode));
    const [ubos, setUbos] = useState([]);
    const [showForm, setShowForm] = useState(Boolean(isUpdating));
    const [editingUboIndex, setEditingUboIndex] = useState(null);
    const [loadingUbos, setLoadingUbos] = useState(false);
    const [phoneCountryOptions, setPhoneCountryOptions] = useState([]);
    const [countriesLoading, setCountriesLoading] = useState(false);
    const [phoneCountryTouched, setPhoneCountryTouched] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [identificationError, setIdentificationError] = useState("");
    const [isSampleModalOpen, setIsSampleModalOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [identificationFrontUpload, setIdentificationFrontUpload] = useState({
        loading: false,
        progress: 0,
        uploaded: false,
    });
    const [identificationBackUpload, setIdentificationBackUpload] = useState({
        loading: false,
        progress: 0,
        uploaded: false,
    });
    const existingOwnershipTotal = ubos.reduce(
        (total, ubo, index) =>
            total + (index === editingUboIndex ? 0 : parseOwnershipShare(getUboOwnership(ubo))),
        0
    );
    const currentOwnershipShare = parseOwnershipShare(form.ownershipShare);
    const ownershipExceedsLimit =
        Boolean(form.ownershipShare) && existingOwnershipTotal + currentOwnershipShare > 100;
    const ownershipBelowMinimum = Boolean(form.ownershipShare) && currentOwnershipShare < 25;
    const ownershipAboveMaximum = Boolean(form.ownershipShare) && currentOwnershipShare > 100;
    const ownershipErrorMessage = ownershipBelowMinimum
        ? "Ownership share must be at least 25%."
        : ownershipAboveMaximum
          ? "Ownership share cannot be more than 100%."
          : ownershipExceedsLimit
            ? "Total UBO ownership cannot exceed 100%."
            : "";
    const ownershipHasError = Boolean(ownershipErrorMessage);

    const fetchBusinessUbos = useCallback(async () => {
        if (!businessId) return;

        try {
            setLoadingUbos(true);
            const response = await handleFetchBusinessById(businessId);
            const business = response?.business || response;
            const fetchedBusinessCountryCode = getRegisteredBusinessCountryCode(business);
            const fetchedBusinessCountryName = getRegisteredBusinessCountryName(business);
            if (fetchedBusinessCountryCode) {
                setBusinessCountryCode(fetchedBusinessCountryCode);
            }
            if (fetchedBusinessCountryName) {
                setBusinessCountryName(fetchedBusinessCountryName);
            }
            const fetchedUbos = Array.isArray(business?.UBOs) ? business.UBOs : [];
            setUbos(fetchedUbos);
            if (shouldReturnToSummary && fetchedUbos.length) {
                setEditingUboIndex(0);
                const mappedForm = mapUboToForm(
                    fetchedUbos[0],
                    fetchedBusinessCountryCode || businessCountryCode
                );
                setForm(mappedForm);
                setIdentificationFrontUpload({
                    loading: false,
                    progress: 100,
                    uploaded: Boolean(mappedForm.identificationDocumentFront),
                });
                setIdentificationBackUpload({
                    loading: false,
                    progress: 100,
                    uploaded: Boolean(mappedForm.identificationDocumentBack),
                });
                setShowForm(true);
            } else if (!isUpdating) {
                // Open the form automatically only when the business has no UBOs yet.
                setShowForm(fetchedUbos.length === 0);
            }
        } catch (error) {
            setErrorMessage(error?.message || "Unable to load control persons.");
        } finally {
            setLoadingUbos(false);
        }
    }, [businessCountryCode, businessId, isUpdating, shouldReturnToSummary]);

    useEffect(() => {
        if (editingUboIndex === null || !phoneCountryOptions.length) return;
        setForm((prev) => {
            const fullPhone = String(prev.phoneNumber || "").replace(/\s/g, "");
            const matchingCountry = [...phoneCountryOptions]
                .sort((a, b) => b.dialCode.length - a.dialCode.length)
                .find((option) => fullPhone.startsWith(option.dialCode.replace(/^\+/, "")));
            if (!matchingCountry) return prev;
            return {
                ...prev,
                phoneCountry: matchingCountry.code,
                phoneNumber: fullPhone.slice(matchingCountry.dialCode.replace(/^\+/, "").length),
            };
        });
    }, [editingUboIndex, phoneCountryOptions]);

    useEffect(() => {
        if (reduxBusinessCountryCode) {
            setBusinessCountryCode(reduxBusinessCountryCode);
        }
        if (reduxBusinessCountryName) {
            setBusinessCountryName(reduxBusinessCountryName);
        }
    }, [reduxBusinessCountryCode, reduxBusinessCountryName]);

    useEffect(() => {
        if (!isUpdating && businessId) {
            fetchBusinessUbos();
        } else if (isUpdating && businessId && !prefillData) {
            fetchBusinessUbos();
        }
    }, [businessId, fetchBusinessUbos, isUpdating, prefillData]);

    useEffect(() => {
        if (!isUpdating || !prefillData) return;
        const mapped = mapUboToForm(
            prefillData,
            businessCountryCode,
            phoneCountryOptions
        );
        if (mapped) {
            setForm(mapped);
            setShowForm(true);
            setIdentificationFrontUpload({
                loading: false,
                progress: 100,
                uploaded: Boolean(mapped.identificationDocumentFront?.url),
            });
            setIdentificationBackUpload({
                loading: false,
                progress: 100,
                uploaded: Boolean(mapped.identificationDocumentBack?.url),
            });
        }
    }, [isUpdating, prefillData, businessCountryCode, phoneCountryOptions]);

    useEffect(() => {
        let ignore = false;

        const fetchCountries = async () => {
            setCountriesLoading(true);
            try {
                const responseBody = await handleFetchKybCountries();
                const countries = normalizeCountriesResponse(responseBody);
                const options = countries
                    .map((item) => {
                        const isoCode = String(item.code || "").toUpperCase();
                        const countryMeta = Country.getCountryByCode(isoCode);
                        const phonecode =
                            item.phoneCode ||
                            item.phonecode ||
                            item.phone?.[0] ||
                            countryMeta?.phonecode;

                        if (!isoCode || !phonecode) return null;

                        const dialCode = String(phonecode).startsWith("+")
                            ? String(phonecode)
                            : `+${phonecode}`;

                        return {
                            value: isoCode,
                            code: isoCode,
                            name: item.name,
                            dialCode,
                            searchLabel: `${item.name} ${isoCode} ${dialCode}`,
                            displayValue: isoCode,
                            label: (
                                <span className="flex w-full items-center gap-2">
                                    <img
                                        src={item.imageUrl}
                                        alt={`${item.name} flag`}
                                        className="size-5 rounded-full object-cover"
                                    />
                                    <span>{isoCode}</span>
                                    <span className="ml-auto text-sm text-[#6F739B]">
                                        {dialCode}
                                    </span>
                                </span>
                            ),
                        };
                    })
                    .filter(Boolean);

                if (!ignore) {
                    setPhoneCountryOptions(options);
                }
            } catch (error) {
                if (!ignore) {
                    setErrorMessage(
                        error?.message ||
                            "Unable to load phone countries. Please refresh and try again."
                    );
                }
            } finally {
                if (!ignore) setCountriesLoading(false);
            }
        };

        fetchCountries();

        return () => {
            ignore = true;
        };
    }, []);

    useEffect(() => {
        if (
            shouldReturnToSummary ||
            editingUboIndex !== null ||
            phoneCountryTouched ||
            (!businessCountryCode && !businessCountryName) ||
            !phoneCountryOptions.length
        )
            return;

        const defaultPhoneCountry =
            phoneCountryOptions.find(
                (option) =>
                    option.code === businessCountryCode ||
                    normalizeCountryName(option.name) === normalizeCountryName(businessCountryName)
            )?.code || "";

        if (!defaultPhoneCountry) return;

        setForm((prev) =>
            prev.phoneCountry === defaultPhoneCountry
                ? prev
                : { ...prev, phoneCountry: defaultPhoneCountry }
        );
    }, [
        businessCountryCode,
        businessCountryName,
        editingUboIndex,
        phoneCountryOptions,
        phoneCountryTouched,
        shouldReturnToSummary,
    ]);

    const updateField = (field, value) => {
        setForm((prev) => ({
            ...prev,
            [field]: value,
            ...(field === "phoneCountry" && !businessRequiresBvn ? { bvn: "" } : {}),
            ...(field === "identificationType"
                ? {
                      identificationDocumentFront: null,
                      identificationDocumentBack: null,
                  }
                : {}),
        }));

        if (field === "identificationType") {
            setIdentificationFrontUpload({ loading: false, progress: 0, uploaded: false });
            setIdentificationBackUpload({ loading: false, progress: 0, uploaded: false });
        }

        if (field === "bvn" || field === "phoneCountry") {
            const nextBvn =
                field === "phoneCountry" && !businessRequiresBvn
                    ? ""
                    : field === "bvn"
                      ? value
                      : form.bvn;

            if (businessRequiresBvn && nextBvn && !isValidBvn(nextBvn)) {
                setIdentificationError("BVN must be exactly 11 digits.");
                return;
            }

            setIdentificationError("");
        }

        if (field === "ownershipShare") {
            setErrorMessage((prev) =>
                prev?.includes("Total UBO ownership cannot exceed 100%") ? "" : prev
            );
        }
    };

    const selectedIdentificationLabel =
        identificationOptions.find((option) => option.value === form.identificationType)?.label ||
        "document";
    const needsBackDocument = form.identificationType !== "passport";

    const getUploadUrl = (raw) =>
        (typeof raw === "string" && raw) ||
        raw?.url ||
        raw?.url?.link ||
        raw?.link ||
        raw?.data?.url ||
        raw?.data?.url?.link ||
        raw?.data?.link ||
        "";

    const getUploadKey = (raw, fallback = "") =>
        raw?.key ||
        raw?.fileKey ||
        raw?.data?.key ||
        raw?.data?.fileKey ||
        raw?.path ||
        raw?.data?.path ||
        fallback;

    const selectedPhoneCountry = phoneCountryOptions.find(
        (option) => option.code === form.phoneCountry
    );
    const requiresBvn = businessRequiresBvn;

    const getPhoneDialCode = () => selectedPhoneCountry?.dialCode || "";
    const getDefaultPhoneCountryCode = () =>
        phoneCountryOptions.find(
            (option) =>
                option.code === businessCountryCode ||
                normalizeCountryName(option.name) === normalizeCountryName(businessCountryName)
        )?.code ||
        (!phoneCountryOptions.length && businessCountryCode) ||
        "";

    const handleIdentificationUpload = async (event, side) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const setUploadState =
            side === "back" ? setIdentificationBackUpload : setIdentificationFrontUpload;
        const field =
            side === "back" ? "identificationDocumentBack" : "identificationDocumentFront";

        setUploadState({ loading: true, progress: 0, uploaded: false });
        updateField(field, file);

        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            setUploadState((prev) => ({
                ...prev,
                progress: Math.min(progress, 95),
            }));
            if (progress >= 95) clearInterval(interval);
        }, 100);

        try {
            const response = await handleUploadOnboardingDocument(file);
            const uploadData = response?.data || response;
            const url = getUploadUrl(uploadData);

            if (!url) {
                throw new Error(response?.message || "No upload URL returned");
            }

            updateField(field, {
                name: file.name,
                size: file.size,
                url,
                key: getUploadKey(uploadData, file.name),
            });
            setUploadState({ loading: false, progress: 100, uploaded: true });
            setErrorMessage("");
        } catch (error) {
            console.error("Control person identification upload failed:", error);
            updateField(field, null);
            setUploadState({ loading: false, progress: 0, uploaded: false });
            setErrorMessage("Upload failed. Please try again with a valid document.");
        } finally {
            clearInterval(interval);
            event.target.value = "";
        }
    };

    const handleRemoveIdentificationDocument = (side) => {
        if (side === "back") {
            updateField("identificationDocumentBack", null);
            setIdentificationBackUpload({ loading: false, progress: 0, uploaded: false });
            return;
        }

        updateField("identificationDocumentFront", null);
        setIdentificationFrontUpload({ loading: false, progress: 0, uploaded: false });
    };

    const isFormFilled = (ubo) => {
        const bvnIsValid = !requiresBvn || (ubo.bvn && isValidBvn(ubo.bvn));

        return Boolean(
            ubo.firstName &&
            ubo.lastName &&
            ubo.email &&
            ubo.phoneNumber &&
            ubo.title &&
            isOwnershipShareValid(ubo.ownershipShare) &&
            bvnIsValid &&
            ubo.gender &&
            ubo.dateOfBirth &&
            ubo.identificationType &&
            ubo.identificationNumber &&
            getUploadUrl(ubo.identificationDocumentFront) &&
            (ubo.identificationType === "passport" ||
                getUploadUrl(ubo.identificationDocumentBack)) &&
            ubo.streetAddress &&
            ubo.city &&
            ubo.state &&
            ubo.postalCode
        );
    };

    const isFormEmpty = (ubo) =>
        !ubo.firstName &&
        !ubo.lastName &&
        !ubo.email &&
        !ubo.phoneNumber &&
        !ubo.title &&
        !ubo.ownershipShare &&
        !ubo.bvn &&
        !ubo.gender &&
        !ubo.dateOfBirth &&
        !ubo.identificationNumber &&
        !ubo.identificationDocumentFront &&
        !ubo.identificationDocumentBack &&
        !ubo.streetAddress &&
        !ubo.city &&
        !ubo.state &&
        !ubo.postalCode;

    const resetCurrentUboForm = () => {
        setPhoneCountryTouched(false);
        setForm(createInitialForm(getDefaultPhoneCountryCode()));
        setIdentificationFrontUpload({ loading: false, progress: 0, uploaded: false });
        setIdentificationBackUpload({ loading: false, progress: 0, uploaded: false });
    };

    const buildUboPayload = () => {
        const cleanPhone = String(form.phoneNumber || "").replace(/[^\d]/g, "");
        const frontDocumentUrl = getUploadUrl(form.identificationDocumentFront);
        const frontDocumentKey = getUploadKey(form.identificationDocumentFront);
        const backDocumentUrl = getUploadUrl(form.identificationDocumentBack);
        const backDocumentKey = getUploadKey(form.identificationDocumentBack);

        return {
            ...(requiresBvn && form.bvn ? { bvn: form.bvn } : {}),
            firstName: form.firstName,
            lastName: form.lastName,
            email: form.email,
            phoneNumber: cleanPhone ? `${getPhoneDialCode()}${cleanPhone}` : "",
            title: form.title,
            meanOfIdentificationType: toApiIdentificationType(form.identificationType),
            meanOfIdentificationUrl: frontDocumentUrl,
            gender: form.gender === "male" ? "Male" : "Female",
            dateOfBirth: form.dateOfBirth,
            percentageOwnership: String(form.ownershipShare),
            idNumber: form.identificationNumber,
            isDirector: form.isDirector,
            address: {
                houseNumber: extractHouseNumber(form.streetAddress),
                street: form.streetAddress,
                city: form.city,
                state: form.state,
                zipCode: form.postalCode,
                subdivision: "",
            },
            idDocumentFront: {
                url: frontDocumentUrl,
                key: frontDocumentKey,
            },
            ...(needsBackDocument && backDocumentUrl
                ? {
                      idDocumentBack: {
                          url: backDocumentUrl,
                          key: backDocumentKey,
                      },
                  }
                : {}),
        };
    };

    useEffect(() => {
        if (!isUpdating || typeof handleUpdateMoreData !== "function") return;

        if (isFormFilled(form) && !ownershipHasError && !ownershipExceedsLimit) {
            const payload = buildUboPayload();
            handleUpdateMoreData("ubo", payload);
        } else {
            handleUpdateMoreData("ubo", {});
        }
    }, [isUpdating, form, ownershipHasError, ownershipExceedsLimit]);

    const handleAddUbo = async ({ navigateAfter = false, keepFormOpen = false } = {}) => {
        if (
            !isFormFilled(form) ||
            identificationFrontUpload.loading ||
            identificationBackUpload.loading ||
            saving
        )
            return false;

        if (!businessId) {
            setErrorMessage("Unable to find business. Please refresh and try again.");
            return false;
        }

        if (ownershipExceedsLimit) {
            setErrorMessage("Total UBO ownership cannot exceed 100%.");
            return false;
        }

        try {
            setSaving(true);
            const payload = buildUboPayload();
            await handleAddBusinessUbo(businessId, payload);
            setErrorMessage("");
            resetCurrentUboForm();
            await fetchBusinessUbos();
            setShowForm(keepFormOpen);

            if (navigateAfter) {
                navigate(
                    shouldReturnToSummary
                        ? "/onboarding/summary"
                        : "/onboarding/business-ownership/directors"
                );
            }

            return true;
        } catch (error) {
            setErrorMessage(error?.message || "Unable to add control person. Please try again.");
            return false;
        } finally {
            setSaving(false);
        }
    };

    const handleContinue = async () => {
        if (showForm && isFormFilled(form)) {
            await handleAddUbo({ navigateAfter: true });
            return;
        }

        if (ubos.length === 0 || !isFormEmpty(form)) return;

        navigate(
            shouldReturnToSummary
                ? "/onboarding/summary"
                : "/onboarding/business-ownership/directors"
        );
    };

    const isUploadingIdentification =
        identificationFrontUpload.loading || identificationBackUpload.loading;
    const canAddUbo =
        isFormFilled(form) && !ownershipHasError && !isUploadingIdentification && !saving;
    const canContinue =
        !isUploadingIdentification &&
        !saving &&
        ((showForm && isFormFilled(form) && !ownershipHasError) ||
            (ubos.length > 0 && isFormEmpty(form)));
    const handleCancelCurrentUbo = () => {
        resetCurrentUboForm();
        setErrorMessage("");
        setShowForm(false);
    };

  const Wrapper = isUpdating ? Fragment : OnboardingLayout;
  const wrapperProps = isUpdating
    ? {}
    : {
      title: "Activate account",
      steps: steps,
      currentStep: currentStep,
      currentSubStep: 0,
      isUpdating: isUpdating,
    };

    return (
      <Wrapper {...wrapperProps}>
        {isUpdating && (
                <Helmet>
                    <title>Add control person</title>
                </Helmet>
            )}

            <div
                className={cn(
                    "max-w-[1200px] relative justify-center",
                    !isUpdating ? "md:pl-96" : ""
                )}
            >
                <div className="max-w-[960px]">
                    {!isUpdating && (
                        <div className="mb-8 flex items-center gap-2">
                            <h2 className="aku-heading-h2-medium text-[#101828]">
                                Add control person (UBO)
                            </h2>
                            <InfoTooltip
                                message="The Ultimate Beneficial Owner is the person who ultimately owns or controls the business."
                                className="text-[#98A2B3] pb-1.5"
                            />
                        </div>
                    )}

                    {loadingUbos && (
                        <div className="mb-4 rounded-lg bg-white p-4 text-sm text-[#667085]">
                            Loading control persons...
                        </div>
                    )}

                    {!isUpdating && ubos.length > 0 && (
                        <div className="mb-4 space-y-3">
                            {ubos.map((ubo, index) => (
                                <div
                                    key={`${ubo.email || "ubo"}-${index}`}
                                    className="flex items-center justify-between rounded-lg bg-white p-4"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="flex size-10 items-center justify-center rounded-full bg-[#F9F5FF] font-semibold text-[#7F56D9]">
                                            {`${ubo.firstName?.charAt(0) || ""}${
                                                ubo.lastName?.charAt(0) || ""
                                            }`.toUpperCase()}
                                        </div>
                                        <div>
                                            <p className="font-semibold text-[#101828]">
                                                UBO {index + 1}: {ubo.firstName} {ubo.lastName}
                                            </p>
                                            <p className="text-sm text-[#667085]">
                                                {ubo.email} • {getUboOwnership(ubo)}% ownership
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {showForm && (
                        <div className="rounded-lg bg-white !p-6">
                            <div className="flex items-center justify-between">
                                <h3
                                    className={cn(
                                        "text-[#101828]",
                                        !isUpdating ? "aku-body-normal-medium" : "text-sm"
                                    )}
                                >
                                    Ultimate Beneficial Owner {!isUpdating ? ubos.length + 1 : null}
                                </h3>

                                {!isUpdating && ubos.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={handleCancelCurrentUbo}
                                        className="text-gray-400 hover:text-red-500"
                                        title="Cancel"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-5 w-5"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M6 18L18 6M6 6l12 12"
                                            />
                                        </svg>
                                    </button>
                                )}
                            </div>

                            {errorMessage && <ErrorMessage message={errorMessage} />}

                            <div className="mt-4">
                                <p className="aku-body-small-medium mb-2 text-[#383B56]">Name</p>
                                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                    <Input
                                        value={form.firstName}
                                        onChange={(value) => updateField("firstName", value)}
                                        placeholder="First name"
                                        className="!mt-0"
                                    />
                                    <Input
                                        value={form.lastName}
                                        onChange={(value) => updateField("lastName", value)}
                                        placeholder="Last name"
                                        className="!mt-0"
                                    />
                                </div>
                            </div>

                            <div className="mt-4">
                                <Input
                                    type="email"
                                    label="Email"
                                    labelClassName="aku-body-small-medium !mb-2 !mt-0 text-[#101828]"
                                    value={form.email}
                                    onChange={(value) => updateField("email", value)}
                                    placeholder="Enter email address"
                                    className="!mt-0"
                                />
                            </div>

                            <div className="mt-4">
                                <p className="aku-body-small-medium mb-2 text-[#383B56]">
                                    Phone number
                                </p>
                                <div className="flex gap-2">
                                    <div className="w-[138px] shrink-0">
                                        <Dropdown
                                            value={form.phoneCountry}
                                            onChange={(value) => {
                                                setPhoneCountryTouched(true);
                                                updateField("phoneCountry", value);
                                            }}
                                            options={phoneCountryOptions}
                                            className="!mt-0"
                                            dropdownClassName="!min-w-[300px]"
                                            searchable
                                            searchPlaceholder="Search country"
                                            placeholder={countriesLoading ? "..." : "--"}
                                            readOnly={countriesLoading}
                                        />
                                    </div>
                                    <div className="flex h-[56px] min-w-0 flex-1 items-center rounded-lg bg-[#f1f5f9] px-4">
                                        <span className="aku-body-normal-medium mr-3 text-[#383B56]">
                                            {getPhoneDialCode()}
                                        </span>
                                        <input
                                            type="tel"
                                            value={form.phoneNumber}
                                            onChange={(event) =>
                                                updateField(
                                                    "phoneNumber",
                                                    event.target.value.replace(/[^\d]/g, "")
                                                )
                                            }
                                            placeholder="905 555 0123"
                                            className="aku-body-normal-regular min-w-0 flex-1 bg-transparent text-main-dark placeholder:text-main-grey outline-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <div>
                                    <Input
                                        label="Title"
                                        labelClassName="aku-body-small-medium !mb-2 !mt-0 text-[#101828]"
                                        value={form.title}
                                        onChange={(value) => updateField("title", value)}
                                        placeholder="e.g. CEO, MD, CFO, etc."
                                        className="!mt-0"
                                    />
                                </div>
                                <div>
                                    <Input
                                        type="text"
                                        inputMode="decimal"
                                        label="Ownership share (in %)"
                                        labelClassName="aku-body-small-medium !mb-2 !mt-0 text-[#101828]"
                                        value={form.ownershipShare}
                                        onChange={(value) =>
                                            updateField(
                                                "ownershipShare",
                                                normalizeOwnershipInput(value)
                                            )
                                        }
                                        placeholder="25.00 %"
                                        inputClassName={
                                            ownershipHasError
                                                ? "!bg-[#FFF1F3] !shadow-[inset_0_0_0_1000px_#FFF1F3] !ring-1 !ring-red-400"
                                                : ""
                                        }
                                        className="!mt-0"
                                    />
                                    {ownershipErrorMessage ? (
                                        <p className="mt-1 text-xs text-red-500">
                                            {ownershipErrorMessage}
                                        </p>
                                    ) : (
                                        <p className="mt-1 text-xs text-[#667085]">
                                            Ownership share must be between 25% and 100%.
                                        </p>
                                    )}
                                </div>
                            </div>

                            {requiresBvn && (
                                <div className="mt-4">
                                    <Input
                                        label="Bank verification number (BVN)"
                                        labelClassName="aku-body-small-medium !mb-2 !mt-0 text-[#101828]"
                                        value={form.bvn}
                                        onChange={(value) => updateField("bvn", value)}
                                        placeholder="e.g. 0864297531"
                                        className="!mt-0"
                                    />
                                    {identificationError && (
                                        <p className="mt-1 text-sm text-red-500">
                                            {identificationError}
                                        </p>
                                    )}
                                </div>
                            )}

                            <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <div>
                                    <p className="aku-body-small-medium mb-2 text-[#383B56]">
                                        Gender
                                    </p>
                                    <Dropdown
                                        value={form.gender}
                                        onChange={(value) => updateField("gender", value)}
                                        options={genderOptions}
                                        placeholder="Select gender"
                                        className="!mt-0"
                                    />
                                </div>
                                <div>
                                    <Input
                                        type="date"
                                        label="Date of birth"
                                        labelClassName="aku-body-small-medium !mb-2 !mt-0 text-[#101828]"
                                        value={form.dateOfBirth}
                                        onChange={(value) => updateField("dateOfBirth", value)}
                                        placeholder="Select date"
                                        min="1900-01-01"
                                        max={getMaxDateOfBirth()}
                                        className="!mt-0"
                                    />
                                </div>
                            </div>

                            <div className="mt-4">
                                <div className="mb-2 flex items-center justify-between">
                                    <p className="aku-body-small-medium text-[#101828]">
                                        Identification document
                                    </p>
                                    <button
                                        type="button"
                                        className="aku-body-normal-medium flex items-center gap-1 text-[#6F00FF]"
                                        onClick={() => setIsSampleModalOpen(true)}
                                    >
                                        See sample{" "}
                                        <span className="material-icons">chevron_right</span>
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                                    <Dropdown
                                        value={form.identificationType}
                                        onChange={(value) =>
                                            updateField("identificationType", value)
                                        }
                                        options={identificationOptions}
                                        className="!mt-0"
                                    />
                                    <Input
                                        value={form.identificationNumber}
                                        onChange={(value) =>
                                            updateField("identificationNumber", value)
                                        }
                                        placeholder={`Enter ${selectedIdentificationLabel.toLowerCase()} ID number`}
                                        className="!mt-0"
                                    />
                                </div>
                                <div
                                    className={`mt-2 grid grid-cols-1 gap-2 ${
                                        needsBackDocument ? "md:grid-cols-2" : ""
                                    }`}
                                >
                                    <UploadInputField
                                        label={
                                            needsBackDocument
                                                ? "Upload front side"
                                                : "Upload passport"
                                        }
                                        uploadTitle={
                                            needsBackDocument
                                                ? "Upload front side"
                                                : `Upload ${selectedIdentificationLabel.toLowerCase()}`
                                        }
                                        hideLabel
                                        fileKey="control-person-identification-front"
                                        fileState={{
                                            file: form.identificationDocumentFront,
                                            loading: identificationFrontUpload.loading,
                                            uploaded: identificationFrontUpload.uploaded,
                                            progress: identificationFrontUpload.progress,
                                        }}
                                        onFileChange={(event) =>
                                            handleIdentificationUpload(event, "front")
                                        }
                                        onRemove={() => handleRemoveIdentificationDocument("front")}
                                        inputBg="#f1f5f9"
                                        uploadedBg="#f1f5f9"
                                        iconBg="#DCCBFF"
                                        titleColor="#161827"
                                        subtitleColor="#9396B4"
                                    />
                                    {needsBackDocument && (
                                        <UploadInputField
                                            label="Upload back side"
                                            uploadTitle="Upload back side"
                                            hideLabel
                                            fileKey="control-person-identification-back"
                                            fileState={{
                                                file: form.identificationDocumentBack,
                                                loading: identificationBackUpload.loading,
                                                uploaded: identificationBackUpload.uploaded,
                                                progress: identificationBackUpload.progress,
                                            }}
                                            onFileChange={(event) =>
                                                handleIdentificationUpload(event, "back")
                                            }
                                            onRemove={() =>
                                                handleRemoveIdentificationDocument("back")
                                            }
                                            inputBg="#f1f5f9"
                                            uploadedBg="#f1f5f9"
                                            iconBg="#DCCBFF"
                                            titleColor="#161827"
                                            subtitleColor="#9396B4"
                                        />
                                    )}
                                </div>
                                <div className="mt-2 flex items-center gap-2 text-[#475467]">
                                    <InfoCircle size="20" color="#2C53F2" variant="Bold" />
                                    <p className="aku-body-small-regular">
                                        All information on the{" "}
                                        {needsBackDocument ? "documents" : "document"} should be
                                        clear and readable
                                    </p>
                                </div>
                            </div>

                            <div className="mt-6">
                                <Input
                                    type="textarea"
                                    label="Residential address"
                                    labelClassName="aku-body-small-medium !mb-1 !mt-0 text-[#101828]"
                                    value={form.streetAddress}
                                    onChange={(value) => updateField("streetAddress", value)}
                                    placeholder="Street address"
                                    className="!mt-0"
                                />
                                <div className="mt-6 grid grid-cols-1 gap-2 md:grid-cols-3">
                                    <Input
                                        value={form.city}
                                        onChange={(value) => updateField("city", value)}
                                        placeholder="City"
                                        className="!mt-0"
                                    />
                                    <Input
                                        value={form.state}
                                        onChange={(value) => updateField("state", value)}
                                        placeholder="State / Province"
                                        className="!mt-0"
                                    />
                                    <Input
                                        value={form.postalCode}
                                        onChange={(value) => updateField("postalCode", value)}
                                        placeholder="Postal / ZIP Code"
                                        className="!mt-0"
                                    />
                                </div>
                            </div>

                            <div className="mt-6">
                                <p className="aku-body-small-medium mb-1 text-[#101828]">
                                    Is this person also a director of the business?
                                </p>
                                <div className="flex items-center gap-8">
                                    <button
                                        type="button"
                                        className="flex items-center gap-2 text-base text-[#101828]"
                                        onClick={() => updateField("isDirector", true)}
                                    >
                                        {form.isDirector ? (
                                            <CheckCircleIcon className="size-5" />
                                        ) : (
                                            <CheckCircleIcon className="size-5 *:fill-[#DBDFE5]" />
                                        )}
                                        Yes
                                    </button>
                                    <button
                                        type="button"
                                        className="flex items-center gap-2 text-base text-[#667085]"
                                        onClick={() => updateField("isDirector", false)}
                                    >
                                        {!form.isDirector ? (
                                            <CheckCircleIcon className="size-5" />
                                        ) : (
                                            <CheckCircleIcon className="size-5 *:fill-[#DBDFE5]" />
                                        )}
                                        No
                                    </button>
                                </div>
                            </div>

                            {!isUpdating && (
                                <button
                                    type="button"
                                    onClick={() => handleAddUbo()}
                                    disabled={!canAddUbo}
                                    className={`!mt-8 rounded-md !px-5 !py-3 text-sm font-semibold ${
                                        canAddUbo
                                            ? "bg-[#7F56D9] text-white hover:bg-[#6f00ff]"
                                            : "cursor-not-allowed bg-[#E9D7FE] text-white"
                                    }`}
                                >
                                    {saving ? <Loader /> : "Save"}
                                </button>
                            )}
                        </div>
                    )}

                    <SampleDocumentModal
                        isOpen={isSampleModalOpen}
                        onClose={() => setIsSampleModalOpen(false)}
                        title={`${selectedIdentificationLabel} sample`}
                        tabs={[]}
                        documentSrc={identificationSampleDocuments[form.identificationType]}
                    />

                    {!isUpdating && (
                        <button
                            type="button"
                            onClick={() => {
                                if (!showForm) {
                                    setShowForm(true);
                                    return;
                                }
                                handleAddUbo({ keepFormOpen: true });
                            }}
                            disabled={showForm && !canAddUbo}
                            className={`mt-8 flex items-center gap-2 text-sm font-medium ${
                                !showForm || canAddUbo
                                    ? "text-[#7F56D9] hover:underline"
                                    : "cursor-not-allowed text-[#B594FF] opacity-60"
                            }`}
                        >
                            <span className="text-xl leading-none">+</span> Add another UBO
                        </button>
                    )}

                    {!isUpdating && (
                        <div className="mt-8 flex justify-end gap-4">
                            <button
                                type="button"
                                className="h-12 rounded-lg px-6 text-base font-normal text-[#101828]"
                                onClick={() =>
                                    navigate("/onboarding/business-verification/wallets")
                                }
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                className={`flex h-12 items-center justify-center rounded-lg px-6 text-base font-normal text-white ${
                                    canContinue ? "bg-[#6F00FF]" : "cursor-not-allowed bg-[#D7C5FF]"
                                }`}
                                onClick={handleContinue}
                                disabled={!canContinue}
                            >
                                {saving ? <Loader /> : "Continue"}
                            </button>
                        </div>
                    )}
                </div>
            </div>
      </Wrapper>
    );
};

export default ControlPerson;
